/**
 * Client CRUD hooks — Phase 3.6.B.
 *
 * Pairs with BE routes:
 *   GET    /api/clients
 *   POST   /api/clients
 *   PATCH  /api/clients/{id}
 *   DELETE /api/clients/{id}
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError, apiClient } from '@/api/client'

import type { Client, ClientInput, ClientUpdateInput } from './types'

export const CLIENTS_QUERY_KEY = ['tenant-clients'] as const

export function useClients() {
  return useQuery({
    queryKey: CLIENTS_QUERY_KEY,
    queryFn: async () => {
      try {
        const res = await apiClient<{ data: Client[] }>('/api/clients')
        return res.data ?? []
      } catch (err) {
        if (err instanceof ApiError && (err.status === 404 || err.status === 501)) {
          return [] as Client[]
        }
        throw err
      }
    },
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: false,
  })
}

export function useCreateClient() {
  const queryClient = useQueryClient()
  return useMutation<Client, Error, ClientInput>({
    mutationFn: async (input) => {
      const res = await apiClient<{ data: Client }>('/api/clients', {
        method: 'POST',
        body: input,
      })
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CLIENTS_QUERY_KEY })
    },
  })
}

export function useUpdateClient() {
  const queryClient = useQueryClient()
  return useMutation<Client, Error, { id: string; input: ClientUpdateInput }>({
    mutationFn: async ({ id, input }) => {
      const res = await apiClient<{ data: Client }>(`/api/clients/${id}`, {
        method: 'PATCH',
        body: input,
      })
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CLIENTS_QUERY_KEY })
    },
  })
}

export function useDeleteClient() {
  const queryClient = useQueryClient()
  return useMutation<void, Error, { id: string }>({
    mutationFn: async ({ id }) => {
      await apiClient(`/api/clients/${id}`, { method: 'DELETE' })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CLIENTS_QUERY_KEY })
    },
  })
}
